import {APP_INITIALIZER, NgModule} from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';

import {TableModule} from 'primeng/table';

import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {HTTP_INTERCEPTORS, HttpClient, HttpClientModule} from '@angular/common/http';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {NgbModule} from '@ng-bootstrap/ng-bootstrap';
import {TranslateLoader, TranslateModule} from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import {ToastComponent} from './components/toast/toast.component';
import {SpinnerComponent} from './shared/spinner/spinner.component';
import {HeaderInterceptor} from './interceptor/header.interceptor';
import {ComponentsComponent} from './components/components/components.component';
import {MapSelectionComponent} from './components/map-selection/map-selection.component';
import {NoauthorizedComponent} from './components/noauthorized/noauthorized.component';
import {ComponentsMaintenanceComponent} from './components/components-maintenance/components-maintenance.component';
import {
  ComponentCreateComponent
} from './components/components-maintenance/component-create/component-create.component';
import {GoogleMapsModule} from '@angular/google-maps';
import {
  ComponentAttributesComponent
} from './components/components-maintenance/component-attributes/component-attributes.component';
import {
  ComponentInstanceComponent
} from './components/components-maintenance/component-instance/component-instance.component';
import {DetailComponentComponent} from './components/components/detail-component/detail-component.component';

import {BrowserAnimationsModule} from '@angular/platform-browser/animations';

import {NgxMaterialTimepickerModule} from "ngx-material-timepicker";
import {NgIf} from "@angular/common";
import {InvalidValuesComponent} from './components/components/dialogs/invalid-values/invalid-values.component';
import {DialogModule} from "primeng/dialog";
import {CardModule} from "primeng/card";
import {HistoricalDialogComponent} from './components/components/dialogs/historical-dialog/historical-dialog.component';
import {TooltipModule} from "primeng/tooltip";
import {TagModule} from "primeng/tag";
import {ConfirmDialogComponent} from "./shared/confirm-dialog/confirm-dialog.component";

import {
  SecureCommInterceptor,
  KeyExchangeService,
  KeyStorageService,
  SECURE_COMM_CONFIG,
} from '@secure-comm/angular';

import { environment } from '../environments/environment';

// Secure-Comm Gateway Configuration
const secureComm = {
  apiBaseUrl: environment.secureComm.apiBaseUrl, // Empty to use relative URLs - proxy will redirect to AKS gateway
  keyExchangeEndpoint: '/crypto/key-exchange',
  encryptAll: false, // Only encrypt URLs matching encryptPatterns (more explicit)
  encryptPatterns: [
    /\/gateway-secure-comm-fm\/.*\/api\/.*/,  // Only encrypt Gateway URLs with /api/ path
  ],
  excludePatterns: [
    // Exceptions to encryptPatterns (URLs that match encryptPatterns but should NOT be encrypted)
    /\/crypto\/key-exchange/,  // Key exchange must be unencrypted
    /\/crypto\/health/,         // Health checks
    /\/actuator\/.*/,           // Spring actuator endpoints
    /\/assets\/.*/,             // Static assets
    /\.json$/,                  // i18n translation files
  ],
  keyRotationHours: 24,
  storageType: 'session' as const,
  debug: true,
  maxTimestampSkew: 3600000, // 60 minutes in milliseconds - CRÍTICO para AKS time differences
};

@NgModule({
  declarations: [
    AppComponent,
    ToastComponent,
    ComponentsComponent,
    SpinnerComponent,
    MapSelectionComponent,
    NoauthorizedComponent,
    ComponentsMaintenanceComponent,
    ComponentCreateComponent,
    ComponentAttributesComponent,
    ComponentInstanceComponent,
    DetailComponentComponent,
    InvalidValuesComponent,
    HistoricalDialogComponent,
    ConfirmDialogComponent
  ],
  imports: [
    BrowserModule,
    HttpClientModule,
    GoogleMapsModule,
    FormsModule,
    ReactiveFormsModule,
    AppRoutingModule,
    TableModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    NgbModule,
    BrowserAnimationsModule,
    NgxMaterialTimepickerModule,
    NgIf,
    TranslateModule,
    DialogModule,
    CardModule,
    TranslateModule,
    TooltipModule,
    TagModule,
    TranslateModule
  ],
  providers: [
    // Secure-Comm Configuration
    {
      provide: SECURE_COMM_CONFIG,
      useValue: secureComm,
    },

    // Secure-Comm Services
    KeyStorageService,
    {
      provide: KeyExchangeService,
      useFactory: keyExchangeServiceFactory,
      deps: [HttpClient, KeyStorageService, SECURE_COMM_CONFIG],
    },

    // HTTP Interceptors
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HeaderInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useFactory: secureCommInterceptorFactory,
      deps: [KeyExchangeService, SECURE_COMM_CONFIG],
      multi: true,
    },

    // APP Initializer for Secure-Comm
    {
      provide: APP_INITIALIZER,
      useFactory: initializeSecureComm,
      deps: [KeyStorageService, KeyExchangeService],
      multi: true,
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
}

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

// Secure-Comm Configuration Factories
export function keyExchangeServiceFactory(
  http: HttpClient,
  storage: KeyStorageService,
  config: any
): KeyExchangeService {
  console.log(
    '🔧 [Factory] Initializing KeyExchangeService with injected config:',
    {
      hasApiBaseUrl: !!config.apiBaseUrl,
      keyExchangeEndpoint: config.keyExchangeEndpoint,
      encryptAll: config.encryptAll,
      maxTimestampSkew: config.maxTimestampSkew,
      debug: config.debug,
      fullConfig: config,
    }
  );
  const service = new KeyExchangeService(http, storage);
  service.initialize(config);
  console.log('🔧 [Factory] Service initialized');
  return service;
}

export function secureCommInterceptorFactory(
  keyExchange: KeyExchangeService,
  config: any
): SecureCommInterceptor {
  console.log(
    '🔧 [Factory] Initializing SecureCommInterceptor with injected config:',
    {
      hasApiBaseUrl: !!config.apiBaseUrl,
      encryptAll: config.encryptAll,
      maxTimestampSkew: config.maxTimestampSkew,
    }
  );
  const interceptor = new SecureCommInterceptor(keyExchange);
  interceptor.initialize(config);
  console.log('🔧 [Factory] Interceptor initialized');
  return interceptor;
}

export function initializeSecureComm(
  storage: KeyStorageService,
  keyExchange: KeyExchangeService
) {
  return async () => {
    await storage.initialize(secureComm.storageType);

    try {
      console.log('🔑 Attempting key exchange with AKS gateway...');
      await keyExchange.performKeyExchange();
      console.log('✅ Key exchange successful');
    } catch (error: any) {
      console.warn(
        '⚠️ Key exchange failed, but continuing anyway:',
        error.message
      );
    }
  };
}
